import React, { useState, useEffect, useMemo } from 'react';
import {
  Calendar, Users, Plus, RotateCcw, Bell, Clock, CheckCircle,
  Circle, AlertCircle, Star, Edit, X, Check, ChevronDown,
  ChevronRight, Trash2, History, Settings, ArrowUpDown,
  TrendingUp, Filter, Save, UserPlus, UsersIcon, ShieldCheck, Camera
} from 'lucide-react';
import { apiClient } from '../utils/api';
import { getTodayDateString } from '../utils/timezoneUtils';
import { RotationGroupsManager } from './admin/RotationGroupsManager';
import { parseChoreSteps as parseSteps, sortedChoreSteps } from '@/utils/choreSteps';
import {
  ChoreRuleEditor, DEFAULT_RULE, describeRule, validateRule, ruleMemberNames, ruleNextLabel,
  type ChoreRule, type Cadence, type RuleMode, type RuleMember,
} from './chores/ChoreRuleEditor';

interface Chore {
  id: string;
  name: string;
  description: string;
  instructions: string;
  category: string;
  base_points: number;
  estimated_minutes: number;
  difficulty: string;
  frequency: string;
  requires_verification: boolean;
  requires_photo?: boolean;
  tags: string[];
  recent_assignments: number;
  rule?: {
    mode: RuleMode;
    member_count: number;
    members?: RuleMember[];
    next?: (RuleMember & { date?: string | null }) | null;
    cadence: Cadence;
    is_active: boolean;
  } | null;
}

interface Child {
  id: string;
  username: string;
  display_name: string;
  // /api/parent/children sends birthdate, age (often empty) and
  // pending_assignments; the card used to read current_age and
  // current_assignments, which it never sends ("Age undefined").
  birthdate?: string | null;
  age?: number | null;
  is_present: boolean;
  pending_assignments?: number;
  total_points: number;
  weekly_points: number;
}

// Categories new families start with. Families can add their own; the list
// offered in the create and edit forms is these plus every category in use.
const DEFAULT_CATEGORIES = [
  'activities', 'bedroom', 'cleaning', 'household', 'kitchen',
  'laundry', 'outdoor', 'pets', 'school',
];
const NEW_CATEGORY = '__new__';

export function childAge(child: Pick<Child, 'birthdate' | 'age'>, today = new Date()): number | null {
  if (child.birthdate) {
    const [y, m, d] = String(child.birthdate).slice(0, 10).split('-').map(Number);
    if (y && m && d) {
      let age = today.getFullYear() - y;
      if (today.getMonth() + 1 < m || (today.getMonth() + 1 === m && today.getDate() < d)) age -= 1;
      if (age >= 0) return age;
    }
  }
  return typeof child.age === 'number' && child.age > 0 ? child.age : null;
}

export function childSummary(child: Pick<Child, 'birthdate' | 'age' | 'pending_assignments'>): string {
  const parts: string[] = [];
  const age = childAge(child);
  if (age !== null) parts.push(`Age ${age}`);
  const n = child.pending_assignments ?? 0;
  parts.push(`${n} ${n === 1 ? 'chore' : 'chores'} to do`);
  return parts.join(' • ');
}

export function categoryOptions(inUse: string[], current?: string): string[] {
  const all = new Set<string>(DEFAULT_CATEGORIES);
  inUse.forEach(c => c && all.add(c));
  if (current && current !== NEW_CATEGORY) all.add(current);
  return Array.from(all).sort((a, b) => a.localeCompare(b));
}

const titleCase = (c: string) => c.charAt(0).toUpperCase() + c.slice(1);

// Pick a category from the list or type a new one ("Morning Chores").
const CategoryField: React.FC<{
  value: string;
  inUse: string[];
  onChange: (v: string) => void;
  className?: string;
}> = ({ value, inUse, onChange, className }) => {
  const [adding, setAdding] = useState(false);
  const options = categoryOptions(inUse, value);
  if (adding) {
    return (
      <div className="flex gap-2">
        <input
          type="text"
          autoFocus
          maxLength={50}
          value={value}
          placeholder="e.g. Morning Chores"
          onChange={e => onChange(e.target.value)}
          className={className}
        />
        <button
          type="button"
          className="px-3 text-sm text-gray-600 border rounded"
          onClick={() => { setAdding(false); onChange(options[0] || 'household'); }}
        >
          Cancel
        </button>
      </div>
    );
  }
  return (
    <select
      value={value}
      onChange={e => {
        if (e.target.value === NEW_CATEGORY) { setAdding(true); onChange(''); }
        else onChange(e.target.value);
      }}
      className={className}
    >
      {options.map(c => <option key={c} value={c}>{titleCase(c)}</option>)}
      <option value={NEW_CATEGORY}>+ New category...</option>
    </select>
  );
};

interface Assignment {
  chore_id: string;
  assigned_to: string | null;
  due_date: string;
  points_offered?: number;
  notes?: string;
  open_mode?: 'claim' | 'bid' | null;
}

interface RotationGroup {
  id: string;
  name: string;
  chore_ids: string[];
  child_ids: string[];
  is_fair: boolean;
  rotation_type: 'weekly' | 'daily' | 'custom';
  active: boolean;
}

type SortOption = 'name' | 'points' | 'category' | 'popularity' | 'time';
type ViewMode = 'grid' | 'list' | 'compact';

export const ParentChoreManager: React.FC = () => {
  const [chores, setChores] = useState<Chore[]>([]);
  const [children, setChildren] = useState<Child[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  // Optional override time for the assign flow. Empty = use each chore's default.
  const [assignDueTime, setAssignDueTime] = useState('');
  const [selectedChores, setSelectedChores] = useState<Assignment[]>([]);

  // Initialize timezone-aware date
  useEffect(() => {
    getTodayDateString().then(setSelectedDate);
  }, []);
  const [selectedChore, setSelectedChore] = useState<Chore | null>(null);
  const [activeTab, setActiveTab] = useState<'assign' | 'manage' | 'rotations'>('assign');
  const [loading, setLoading] = useState(false);
  const [editingChoreId, setEditingChoreId] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('category');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [openAssignMode, setOpenAssignMode] = useState<'claim' | 'bid'>('claim');
  const [biddingEnabled, setBiddingEnabled] = useState(false);
  const [showCreateChore, setShowCreateChore] = useState(false);
  const [rotationGroups, setRotationGroups] = useState<RotationGroup[]>([]);
  const [editingRotationId, setEditingRotationId] = useState<string | null>(null);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedChoreIds, setSelectedChoreIds] = useState<Set<string>>(new Set());
  const [usingMockRotationData, setUsingMockRotationData] = useState(false);
  // Per-chore assignment rules (chore_rules feature toggle)
  const [rulesEnabled, setRulesEnabled] = useState(false);
  const [editRuleOn, setEditRuleOn] = useState(false);
  const [editRuleExisted, setEditRuleExisted] = useState(false);
  const [editRule, setEditRule] = useState<ChoreRule>(DEFAULT_RULE);
  const [showBulkRule, setShowBulkRule] = useState(false);
  const [bulkRule, setBulkRule] = useState<ChoreRule>(DEFAULT_RULE);
  const [bulkRuleError, setBulkRuleError] = useState<string | null>(null);
  const [bulkRuleConflict, setBulkRuleConflict] = useState(false);
  
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    instructions: '',
    base_points: 0,
    estimated_minutes: 0,
    difficulty: 'easy',
    category: 'household',
    steps: [] as { position: number; text: string }[],
    requires_verification: false,
    requires_photo: false,
    default_due_time: '',
  });
  const [showEditModal, setShowEditModal] = useState(false);

  const [newChore, setNewChore] = useState({
    name: '',
    description: '',
    instructions: '',
    category: 'household',
    base_points: 10,
    estimated_minutes: 15,
    difficulty: 'easy',
    frequency: 'once',
    requires_verification: false,
    requires_photo: false,
    default_due_time: '',
    tags: []
  });

  const [newRotation, setNewRotation] = useState({
    name: '',
    chore_ids: [] as string[],
    child_ids: [] as string[],
    is_fair: true,
    rotation_type: 'weekly' as 'weekly' | 'daily' | 'custom'
  });

  useEffect(() => {
    loadChores();
    loadChildren();
    if (activeTab === 'rotations') {
      loadRotationGroups();
    }
  }, [selectedDate, activeTab]);

  useEffect(() => {
    apiClient.get('/api/family/features')
      .then((data: any) => {
        const features = Array.isArray(data) ? data : data?.data || [];
        const bidding = features.find((f: any) => f.feature_key === 'chore_bidding');
        setBiddingEnabled(bidding?.enabled === true);
        const rules = features.find((f: any) => f.feature_key === 'chore_rules');
        setRulesEnabled(rules?.enabled === true);
      })
      .catch(() => setBiddingEnabled(false));
  }, []);

  const loadChores = async () => {
    try {
      const chores = await apiClient.getParentChores();
      setChores(chores);
    } catch (error: any) {
      console.error('Error loading chores:', error);
    }
  };

  const loadChildren = async () => {
    try {
      const children = await apiClient.getParentChildren(selectedDate);
      setChildren(children);
    } catch (error: any) {
      console.error('Error loading children:', error);
    }
  };

  const loadRotationGroups = async () => {
    try {
      const groups = await apiClient.get('/api/rotation-groups');
      // Accept both empty array and populated array from API
      if (Array.isArray(groups)) {
        // Ensure chore_ids and child_ids are always arrays
        const sanitizedGroups = groups.map((g: RotationGroup) => ({
          ...g,
          chore_ids: g.chore_ids || [],
          child_ids: g.child_ids || []
        }));
        setRotationGroups(sanitizedGroups);
        setUsingMockRotationData(false);
      } else {
        // If response is not an array, show empty state
        setRotationGroups([]);
        setUsingMockRotationData(false);
      }
    } catch (error: any) {
      console.error('Error loading rotation groups:', error);
      // Only on actual API error, show empty state (not mock data)
      setRotationGroups([]);
      setUsingMockRotationData(false);
    }
  };

  // Sort and group chores
  const processedChores = useMemo(() => {
    let filtered = chores;
    
    // Apply category filter
    if (filterCategory !== 'all') {
      filtered = chores.filter(c => c.category === filterCategory);
    }
    
    // Sort chores
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'points':
          return b.base_points - a.base_points;
        case 'popularity':
          return b.recent_assignments - a.recent_assignments;
        case 'time':
          return a.estimated_minutes - b.estimated_minutes;
        case 'category':
        default:
          return a.category.localeCompare(b.category);
      }
    });
    
    // Group by category
    const grouped: Record<string, Chore[]> = {};
    sorted.forEach(chore => {
      if (!grouped[chore.category]) {
        grouped[chore.category] = [];
      }
      grouped[chore.category].push(chore);
    });
    
    return grouped;
  }, [chores, sortBy, filterCategory]);

  // Get categories
  const categories = useMemo(() => {
    const cats = new Set(chores.map(c => c.category));
    return Array.from(cats).sort();
  }, [chores]);

  // Get popular chores (assigned in last 3 months)
  const popularChores = useMemo(() => {
    return chores
      .filter(c => c.recent_assignments > 0)
      .sort((a, b) => b.recent_assignments - a.recent_assignments)
      .slice(0, 10);
  }, [chores]);

  const assignChores = async () => {
    if (selectedChores.length === 0) return;

    setLoading(true);
    try {
      // A blank time sends date-only, so the backend applies each chore's
      // default due time; a set time overrides it for this batch.
      const dueDate = assignDueTime ? `${selectedDate}T${assignDueTime}:00` : selectedDate;
      const response = await apiClient.assignChores(
        selectedChores.map(assignment => ({
          ...assignment,
          due_date: dueDate
        }))
      );

      alert(`Successfully assigned ${response.assignments.length} chores!`);
      setSelectedChores([]);
      setSelectedChore(null);
      loadChildren();
    } catch (error: any) {
      alert(`Error assigning chores: ${error.response?.data?.error || error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChore = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await apiClient.createNewChore(newChore);
      alert('Chore created successfully!');
      setShowCreateChore(false);
      setNewChore({
        name: '',
        description: '',
        instructions: '',
        category: 'household',
        base_points: 10,
        estimated_minutes: 15,
        difficulty: 'easy',
        frequency: 'once',
        requires_verification: false,
        requires_photo: false,
        default_due_time: '',
        tags: []
      });
      loadChores();
    } catch (error: any) {
      alert(`Error creating chore: ${error.response?.data?.error || error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (chore: Chore) => {
    setEditingChoreId(chore.id);
    setEditForm({
      name: chore.name,
      description: chore.description || '',
      instructions: chore.instructions || '',
      base_points: chore.base_points,
      estimated_minutes: chore.estimated_minutes || 15,
      difficulty: chore.difficulty,
      category: chore.category,
      steps: parseSteps((chore as any).steps),
      requires_verification: (chore as any).requires_verification || false,
      requires_photo: (chore as any).requires_photo || false,
      default_due_time: (chore as any).default_due_time
        ? String((chore as any).default_due_time).slice(0, 5)
        : '',
    });
    setEditRuleOn(false);
    setEditRuleExisted(false);
    setEditRule({ ...DEFAULT_RULE, member_ids: children.map(c => c.id) });
    if (rulesEnabled && chore.rule) {
      apiClient.get(`/api/chores/${chore.id}/rule`)
        .then((data: any) => {
          if (data?.rule) {
            setEditRule({ ...DEFAULT_RULE, ...data.rule });
            setEditRuleOn(true);
            setEditRuleExisted(true);
          }
        })
        .catch(() => {});
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editingChoreId) return;
    
    if (rulesEnabled && editRuleOn) {
      const problem = validateRule(editRule);
      if (problem) {
        alert(problem);
        return;
      }
    }

    try {
      await apiClient.updateChore(editingChoreId, editForm);
      if (rulesEnabled && editRuleOn) {
        await apiClient.put(`/api/chores/${editingChoreId}/rule`, { rule: editRule });
      } else if (rulesEnabled && editRuleExisted) {
        await apiClient.delete(`/api/chores/${editingChoreId}/rule`);
      }
      await loadChores();
      setEditingChoreId(null);
      setShowEditModal(false);
      alert('Chore updated successfully!');
    } catch (error: any) {
      alert(`Failed to update chore: ${error.response?.data?.error || error.response?.data?.detail || error.message}`);
    }
  };

  // Apply one rule to every selected chore. On a rotation-group conflict the
  // parent can take the chores out of their groups and retry in one tap.
  const handleBulkRule = async (removeFromGroups = false) => {
    const problem = validateRule(bulkRule);
    if (problem) {
      setBulkRuleError(problem);
      return;
    }
    const ids = Array.from(selectedChoreIds);
    setLoading(true);
    setBulkRuleError(null);
    try {
      if (removeFromGroups) {
        const groups = await apiClient.get('/api/rotation-groups');
        for (const g of Array.isArray(groups) ? groups : []) {
          const kept = (g.chore_ids || []).filter((id: string) => !ids.includes(id));
          if (kept.length !== (g.chore_ids || []).length) {
            await apiClient.patch(`/api/rotation-groups/${g.id}`, { chore_ids: kept });
          }
        }
      }
      const res = await apiClient.post('/api/chores/rules/bulk', { chore_ids: ids, rule: bulkRule });
      setShowBulkRule(false);
      setBulkRuleConflict(false);
      setSelectedChoreIds(new Set());
      setBulkEditMode(false);
      await loadChores();
      alert(`Set up ${res?.rules?.length ?? ids.length} chores. ${res?.sync?.inserted ?? 0} upcoming chores were scheduled.`);
    } catch (error: any) {
      setBulkRuleConflict(error.response?.status === 409);
      setBulkRuleError(error.response?.data?.error || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChore = async (choreId: string) => {
    if (!confirm('Are you sure you want to delete this chore?')) return;
    
    try {
      await apiClient.deleteChore(choreId);
      await loadChores();
      alert('Chore deleted successfully!');
    } catch (error: any) {
      alert(`Failed to delete chore: ${error.response?.data?.error || error.response?.data?.detail || error.message}`);
    }
  };

  const handleBulkPointsUpdate = async () => {
    if (selectedChoreIds.size === 0) {
      alert('Please select chores to update');
      return;
    }
    
    const newPoints = prompt('Enter new point value for selected chores:');
    if (!newPoints || isNaN(Number(newPoints))) return;
    
    setLoading(true);
    try {
      for (const choreId of selectedChoreIds) {
        await apiClient.updateChore(choreId, { base_points: Number(newPoints) });
      }
      await loadChores();
      setSelectedChoreIds(new Set());
      setBulkEditMode(false);
      alert(`Updated ${selectedChoreIds.size} chores successfully!`);
    } catch (error: any) {
      alert(`Failed to update chores: ${error.response?.data?.error || error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const addToAssignment = (chore: Chore, childId: string | null) => {
    const assignment: Assignment = {
      chore_id: chore.id,
      assigned_to: childId,
      due_date: selectedDate,
      points_offered: chore.base_points,
      open_mode: childId === null ? openAssignMode : null
    };

    setSelectedChores([...selectedChores, assignment]);
  };

  // Adds the chore for several kids in one state update (calling
  // addToAssignment in a loop would keep only the last kid).
  const addForKids = (chore: Chore, childIds: string[]) => {
    setSelectedChores(prev => {
      const already = new Set(prev.filter(a => a.chore_id === chore.id).map(a => a.assigned_to));
      const added = childIds
        .filter(id => !already.has(id))
        .map(id => ({
          chore_id: chore.id,
          assigned_to: id,
          due_date: selectedDate,
          points_offered: chore.base_points,
          open_mode: null,
        }));
      return [...prev, ...added];
    });
  };

  const removeKidFromChore = (choreId: string, childId: string) => {
    setSelectedChores(prev => prev.filter(a => !(a.chore_id === choreId && a.assigned_to === childId)));
  };

  const removeFromAssignment = (index: number) => {
    setSelectedChores(selectedChores.filter((_, i) => i !== index));
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'routine': return <Circle className="w-4 h-4" />;
      case 'open': return <CheckCircle className="w-4 h-4" />;
      case 'premium': return <Star className="w-4 h-4" />;
      default: return <Circle className="w-4 h-4" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Parent Chore Manager</h1>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <Calendar className="w-5 h-5 text-gray-500" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border rounded px-3 py-2"
              />
            </div>
            {activeTab === 'assign' && (
              <div className="flex items-center space-x-2">
                <Clock className="w-5 h-5 text-gray-500" />
                <input
                  type="time"
                  value={assignDueTime}
                  onChange={(e) => setAssignDueTime(e.target.value)}
                  className="border rounded px-3 py-2"
                  title="Due time (optional). Leave blank to use each chore's default."
                />
                <span className="text-xs text-gray-500">Due time (blank = chore default)</span>
              </div>
            )}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setActiveTab('assign')}
              className={`px-4 py-2 rounded ${activeTab === 'assign' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Assign Chores
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`px-4 py-2 rounded ${activeTab === 'manage' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Manage Chores
            </button>
            <button
              onClick={() => setActiveTab('rotations')}
              className={`px-4 py-2 rounded ${activeTab === 'rotations' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            >
              Rotation Groups
            </button>
          </div>
        </div>
      </div>

      {/* Assign Chores Tab */}
      {activeTab === 'assign' && (
        <div className="space-y-6">
          {/* Controls Bar */}
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Filter className="w-4 h-4 text-gray-500" />
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="all">All Categories</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <ArrowUpDown className="w-4 h-4 text-gray-500" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as SortOption)}
                    className="border rounded px-2 py-1 text-sm"
                  >
                    <option value="category">Sort by Category</option>
                    <option value="name">Sort by Name</option>
                    <option value="points">Sort by Points</option>
                    <option value="popularity">Sort by Popularity</option>
                    <option value="time">Sort by Time</option>
                  </select>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {selectedChores.length > 0 && (
                  <span className="text-sm text-gray-600">
                    {selectedChores.length} chores selected
                  </span>
                )}
                <button
                  onClick={assignChores}
                  disabled={selectedChores.length === 0 || loading}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  Assign Selected Chores
                </button>
              </div>
            </div>
          </div>

          {/* Popular Chores Quick Access */}
          {popularChores.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold mb-3 flex items-center">
                <TrendingUp className="w-4 h-4 mr-2" />
                Frequently Assigned
              </h3>
              <div className="flex flex-wrap gap-2">
                {popularChores.map(chore => (
                  <button
                    key={chore.id}
                    onClick={() => setSelectedChore(chore)}
                    className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm hover:bg-blue-200"
                  >
                    {chore.name} ({chore.recent_assignments})
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Chores by Category */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Select Chores</h2>
              <div className="space-y-2 max-h-[600px] overflow-y-auto">
                {Object.entries(processedChores).map(([category, categoryChores]) => (
                  <div key={category} className="border rounded-lg">
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        {expandedCategories.has(category) ? 
                          <ChevronDown className="w-4 h-4 mr-2" /> : 
                          <ChevronRight className="w-4 h-4 mr-2" />
                        }
                        <span className="font-medium capitalize">{category}</span>
                        <span className="ml-2 text-sm text-gray-500">({categoryChores.length})</span>
                      </div>
                    </button>
                    
                    {expandedCategories.has(category) && (
                      <div className="px-4 pb-2 space-y-1">
                        {categoryChores.map(chore => (
                          <div
                            key={chore.id}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                            onClick={() => setSelectedChore(chore)}
                          >
                            <div className="flex-1">
                              <div className="font-medium text-sm">{chore.name}</div>
                              <div className="text-xs text-gray-500">
                                {chore.base_points} pts • {chore.estimated_minutes} min
                              </div>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs ${getDifficultyColor(chore.difficulty)}`}>
                              {chore.difficulty}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Children Present */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold mb-4">Children Present on {selectedDate}</h2>

              {/* Open to all chores */}
              {selectedChores.filter(a => a.assigned_to === null).length > 0 && (
                <div className="mb-4 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                  <h3 className="font-medium text-purple-800 mb-2">🌟 Open to All Kids</h3>
                  <div className="space-y-2">
                    {selectedChores.filter(a => a.assigned_to === null).map((assignment, idx) => {
                      const chore = chores.find(c => c.id === assignment.chore_id);
                      return (
                        <div key={idx} className="flex items-center justify-between text-sm bg-white rounded p-2">
                          <span className="font-medium">{chore?.name}</span>
                          <button
                            onClick={() => {
                              const index = selectedChores.findIndex(
                                a => a.chore_id === assignment.chore_id && a.assigned_to === null
                              );
                              removeFromAssignment(index);
                            }}
                            className="text-red-500 hover:text-red-700 text-xs"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="space-y-3">
                {children.filter(child => child.is_present).map((child) => {
                  const childAssignments = selectedChores.filter(a => a.assigned_to === child.id);
                  return (
                    <div key={child.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <h3 className="font-medium">{child.display_name}</h3>
                          <p className="text-sm text-gray-600">
                            {childSummary(child)}
                          </p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-blue-600">{child.total_points} pts</div>
                          <div className="text-xs text-gray-500">+{child.weekly_points} this week</div>
                        </div>
                      </div>
                      
                      {childAssignments.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <div className="text-xs text-gray-600 mb-1">Assigned for {selectedDate}:</div>
                          {childAssignments.map((assignment, idx) => {
                            const chore = chores.find(c => c.id === assignment.chore_id);
                            return (
                              <div key={idx} className="flex items-center justify-between text-sm">
                                <span>{chore?.name}</span>
                                <button
                                  onClick={() => {
                                    const index = selectedChores.findIndex(
                                      a => a.chore_id === assignment.chore_id && a.assigned_to === child.id
                                    );
                                    removeFromAssignment(index);
                                  }}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  Remove
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
                
                {children.filter(child => !child.is_present).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-medium text-gray-500 mb-2">Not Present Today</h4>
                    {children.filter(child => !child.is_present).map((child) => (
                      <div key={child.id} className="text-sm text-gray-400 py-1">
                        {child.display_name} (not present)
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manage Chores Tab */}
      {activeTab === 'manage' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Manage Chores</h2>
              <div className="flex space-x-2">
                <button
                  onClick={() => setBulkEditMode(!bulkEditMode)}
                  className={`px-4 py-2 rounded ${bulkEditMode ? 'bg-yellow-600 text-white' : 'bg-gray-200'}`}
                >
                  {bulkEditMode ? 'Done Selecting' : 'Select Chores'}
                </button>
                {bulkEditMode && selectedChoreIds.size > 0 && (
                  <button
                    onClick={handleBulkPointsUpdate}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    Set Points ({selectedChoreIds.size})
                  </button>
                )}
                {rulesEnabled && bulkEditMode && selectedChoreIds.size > 0 && (
                  <button
                    onClick={() => {
                      setBulkRule({ ...DEFAULT_RULE, member_ids: children.map(c => c.id) });
                      setBulkRuleError(null);
                      setBulkRuleConflict(false);
                      setShowBulkRule(true);
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Set Who Does It ({selectedChoreIds.size})
                  </button>
                )}
                <button
                  onClick={() => setShowCreateChore(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 inline mr-2" />
                  Create New Chore
                </button>
              </div>
            </div>

            <div className="space-y-2">
              {Object.entries(
                chores.reduce((acc: Record<string, Chore[]>, chore) => {
                  const cat = chore.category || 'Other'
                  if (!acc[cat]) acc[cat] = []
                  acc[cat].push(chore)
                  return acc
                }, {} as Record<string, Chore[]>)
              ).sort(([a], [b]) => a.localeCompare(b)).map(([category, categoryChores]) => (
                <div key={category} className="mb-2">
                  <button
                    type="button"
                    onClick={() => {
                      setExpandedCategories(prev => {
                        const next = new Set(prev)
                        next.has(category) ? next.delete(category) : next.add(category)
                        return next
                      })
                    }}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <span className="font-semibold text-gray-700 capitalize">{category}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{categoryChores.length} chores</span>
                      <span className="text-gray-400">{expandedCategories.has(category) ? '▼' : '▶'}</span>
                    </div>
                  </button>
                  {expandedCategories.has(category) && categoryChores.map(chore => (
                <div key={chore.id} className="border rounded-lg p-4 ml-2 mt-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {bulkEditMode && (
                          <input
                            type="checkbox"
                            checked={selectedChoreIds.has(chore.id)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedChoreIds);
                              if (e.target.checked) {
                                newSelected.add(chore.id);
                              } else {
                                newSelected.delete(chore.id);
                              }
                              setSelectedChoreIds(newSelected);
                            }}
                            className="rounded"
                          />
                        )}
                        <div>
                          <div className="font-medium">{chore.name}</div>
                          <div className="text-sm text-gray-600">
                            {chore.category} • {chore.base_points} pts • {chore.estimated_minutes} min • {chore.difficulty}
                          </div>
                          {(chore.requires_verification || chore.requires_photo) && (
                            <div className="flex gap-1 mt-1" data-testid="chore-proof-badges">
                              {chore.requires_verification && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">
                                  <ShieldCheck className="w-3 h-3" /> Needs parent check
                                </span>
                              )}
                              {chore.requires_photo && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-sky-100 text-sky-800">
                                  <Camera className="w-3 h-3" /> Photo proof
                                </span>
                              )}
                            </div>
                          )}
                          {chore.description && (
                            <div className="text-xs text-gray-500 mt-1">{chore.description}</div>
                          )}
                          {rulesEnabled && chore.rule && (
                            <div className="text-xs mt-1" data-testid="chore-rule-summary">
                              <div className="text-indigo-700">
                                {describeRule(chore.rule)}
                                {!chore.rule.is_active && <span className="text-gray-500"> (paused)</span>}
                              </div>
                              {ruleMemberNames(chore.rule) && (
                                <div className="text-gray-600">
                                  {chore.rule.mode === 'rotation' ? 'Order: ' : 'Members: '}
                                  {ruleMemberNames(chore.rule)}
                                </div>
                              )}
                              {chore.rule.is_active && ruleNextLabel(chore.rule) && (
                                <div className="text-indigo-900 font-medium">{ruleNextLabel(chore.rule)}</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditClick(chore)}
                          className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit chore"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteChore(chore.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                          title="Delete chore"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                </div>
              ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Rotation Groups Tab */}
      {activeTab === 'rotations' && (
        <RotationGroupsManager />
      )}

      {/* Assignment Modal */}
      {selectedChore && activeTab === 'assign' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-8 z-50">
          <div className="bg-white rounded-xl p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-2xl font-bold">{selectedChore.name}</h2>
                <p className="text-gray-600">{selectedChore.description}</p>
              </div>
              <button
                onClick={() => setSelectedChore(null)}
                className="text-gray-400 hover:text-gray-600 text-3xl"
              >
                ×
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{selectedChore.base_points}</div>
                <div className="text-sm text-gray-500">Points</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{selectedChore.estimated_minutes}</div>
                <div className="text-sm text-gray-500">Minutes</div>
              </div>
              <div className="text-center">
                <div className={`text-2xl font-bold ${getDifficultyColor(selectedChore.difficulty).split(' ')[0]}`}>
                  {selectedChore.difficulty}
                </div>
                <div className="text-sm text-gray-500">Difficulty</div>
              </div>
            </div>

            {selectedChore.instructions && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Instructions:</h3>
                <pre className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 p-3 rounded">
                  {selectedChore.instructions}
                </pre>
              </div>
            )}

            {parseSteps((selectedChore as any).steps).length > 0 && (
              <div className="mb-4">
                <h3 className="font-semibold mb-2">Steps:</h3>
                <ol className="list-decimal ml-5 space-y-1 text-sm text-gray-700">
                  {parseSteps((selectedChore as any).steps).sort((a, b) => a.position - b.position).map((step, i) => (
                    <li key={i}>{step.text}</li>
                  ))}
                </ol>
              </div>
            )}

            <div className="mb-4 flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Assign for date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-3">Quick Assign to Children:</h3>

              {/* Open to all kids option */}
              <div className="mb-4 border-2 border-purple-200 rounded-lg p-4 bg-purple-50">
                {selectedChores.some(a => a.chore_id === selectedChore.id && a.assigned_to === null) ? (
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-purple-800">Open to All Kids</h4>
                      <p className="text-sm text-purple-600">
                        {selectedChores.find(a => a.chore_id === selectedChore.id && a.assigned_to === null)?.open_mode === 'bid'
                          ? 'Kids will bid on this chore'
                          : 'First kid to claim gets it'}
                      </p>
                    </div>
                    <span className="text-purple-600 font-semibold">Added</span>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-purple-800">Open to All Kids</h4>
                        <p className="text-sm text-purple-600">
                          {openAssignMode === 'claim' ? 'First kid to claim gets it' : 'Kids bid points to win it'}
                        </p>
                      </div>
                      <button
                        onClick={() => {
                          addToAssignment(selectedChore, null);
                        }}
                        className="bg-purple-600 text-white px-4 py-2 rounded text-sm hover:bg-purple-700 font-medium"
                      >
                        Make Open
                      </button>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button
                        onClick={() => setOpenAssignMode('claim')}
                        className={`px-3 py-1 rounded text-xs font-medium ${openAssignMode === 'claim' ? 'bg-green-600 text-white' : 'bg-white text-gray-600 border'}`}
                      >
                        First to Claim
                      </button>
                      {biddingEnabled && (
                        <button
                          onClick={() => setOpenAssignMode('bid')}
                          className={`px-3 py-1 rounded text-xs font-medium ${openAssignMode === 'bid' ? 'bg-amber-500 text-white' : 'bg-white text-gray-600 border'}`}
                        >
                          Bidding
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {(() => {
                const homeKids = children.filter(c => c.is_present);
                const missing = homeKids.filter(
                  c => !selectedChores.some(a => a.chore_id === selectedChore.id && a.assigned_to === c.id)
                );
                return homeKids.length > 1 ? (
                  <div className="flex items-center justify-between mb-3 p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="text-sm text-blue-900">
                      Each kid gets their own copy of this chore.
                    </div>
                    <button
                      onClick={() => addForKids(selectedChore, missing.map(c => c.id))}
                      disabled={missing.length === 0}
                      className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 font-medium disabled:opacity-50"
                    >
                      {missing.length === 0 ? 'All kids added' : `Add for all ${homeKids.length} kids`}
                    </button>
                  </div>
                ) : null;
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[...children].sort((a, b) => Number(b.is_present) - Number(a.is_present)).map((child) => {
                  const isAssigned = selectedChores.some(
                    a => a.chore_id === selectedChore.id && a.assigned_to === child.id
                  );
                  return (
                    <div key={child.id} className={`border rounded-lg p-3 ${child.is_present ? '' : 'opacity-60'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{child.display_name}</h4>
                          <p className="text-sm text-gray-600">
                            {child.is_present
                              ? childSummary(child)
                              : 'Away that day'}
                          </p>
                        </div>
                        {isAssigned ? (
                          <button
                            onClick={() => removeKidFromChore(selectedChore.id, child.id)}
                            className="text-green-600 font-semibold hover:text-red-600"
                            title="Remove"
                          >
                            ✓ Added
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              addToAssignment(selectedChore, child.id);
                            }}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700"
                          >
                            Add
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex space-x-2 mt-6">
              <button
                onClick={() => setSelectedChore(null)}
                className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
              >
                Close
              </button>
              <button
                onClick={() => {
                  const chore = selectedChore;
                  setSelectedChore(null);
                  if (chore) handleEditClick(chore);
                }}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Edit Chore
              </button>
              {selectedChores.length > 0 && (
                <button
                  onClick={() => {
                    assignChores();
                    setSelectedChore(null);
                  }}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Assign All ({selectedChores.length} chores)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Chore Modal */}
      {showCreateChore && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-8 z-50">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-2xl font-bold">Create New Chore</h2>
              <button
                onClick={() => setShowCreateChore(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleCreateChore} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chore Name *
                  </label>
                  <input
                    type="text"
                    value={newChore.name}
                    onChange={(e) => setNewChore({...newChore, name: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category *
                  </label>
                  <CategoryField
                    value={newChore.category}
                    inUse={chores.map(c => c.category)}
                    onChange={(category) => setNewChore({...newChore, category})}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={newChore.description}
                  onChange={(e) => setNewChore({...newChore, description: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions
                </label>
                <textarea
                  value={newChore.instructions}
                  onChange={(e) => setNewChore({...newChore, instructions: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  placeholder="Step-by-step instructions..."
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Points *
                  </label>
                  <input
                    type="number"
                    value={newChore.base_points || ''}
                    onChange={(e) => setNewChore({...newChore, base_points: parseInt(e.target.value.replace(/^0+/, '') || '0') || 0})}
                    className="w-full border rounded px-3 py-2"
                    min="1"
                    max="100"
                    required
                    placeholder="10"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time (minutes)
                  </label>
                  <input
                    type="number"
                    value={newChore.estimated_minutes || ''}
                    onChange={(e) => setNewChore({...newChore, estimated_minutes: parseInt(e.target.value.replace(/^0+/, '') || '0') || 0})}
                    className="w-full border rounded px-3 py-2"
                    min="5"
                    max="180"
                    placeholder="15"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Difficulty
                  </label>
                  <select
                    value={newChore.difficulty}
                    onChange={(e) => setNewChore({...newChore, difficulty: e.target.value})}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default due time
                </label>
                <input
                  type="time"
                  value={newChore.default_due_time}
                  onChange={(e) => setNewChore({...newChore, default_due_time: e.target.value})}
                  className="w-full border rounded px-3 py-2"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional. Applied when this chore is assigned or auto-scheduled. Blank = 8 PM.
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateChore(false)}
                  className="px-4 py-2 border rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Creating...' : 'Create Chore'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bulk assignment rule modal */}
      {showBulkRule && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-1">
              <h3 className="text-lg font-semibold">Who does these {selectedChoreIds.size} chores?</h3>
              <button onClick={() => setShowBulkRule(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              The same rule goes on every selected chore. Upcoming chores update right away.
            </p>
            <ChoreRuleEditor kids={children} value={bulkRule} onChange={setBulkRule} />
            {bulkRuleError && (
              <div className="mt-4 p-3 rounded-lg bg-red-50 text-sm text-red-700">
                {bulkRuleError}
                {bulkRuleConflict && (
                  <button
                    type="button"
                    onClick={() => handleBulkRule(true)}
                    disabled={loading}
                    className="block mt-2 font-medium text-red-800 underline"
                  >
                    Remove these chores from their rotation groups and continue
                  </button>
                )}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button onClick={() => setShowBulkRule(false)} className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={() => handleBulkRule(false)} disabled={loading} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                Apply to {selectedChoreIds.size} chores
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Chore Modal */}
      {showEditModal && editingChoreId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Edit Chore</h3>
              <button onClick={() => { setShowEditModal(false); setEditingChoreId(null); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Name</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm({...editForm, name: e.target.value})} className="w-full px-3 py-2 border rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Category</label>
                  <CategoryField
                    value={editForm.category}
                    inUse={chores.map(c => c.category)}
                    onChange={(category) => setEditForm({...editForm, category})}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={2} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Instructions</label>
                <textarea value={editForm.instructions} onChange={(e) => setEditForm({...editForm, instructions: e.target.value})} className="w-full px-3 py-2 border rounded-lg" rows={3} />
              </div>

              {/* Steps */}
              <div>
                <label className="block text-sm font-medium mb-1">Steps</label>
                <div className="space-y-2">
                  {editForm.steps.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="text-sm text-gray-400 w-6">{idx + 1}.</span>
                      <input type="text" value={step.text} onChange={(e) => {
                        const updated = [...editForm.steps]; updated[idx] = { ...updated[idx], text: e.target.value }; setEditForm({ ...editForm, steps: updated });
                      }} className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Step description..." />
                      <button type="button" onClick={() => { if (idx > 0) { const u = [...editForm.steps]; [u[idx-1], u[idx]] = [u[idx], u[idx-1]]; u.forEach((s,i) => s.position = i+1); setEditForm({...editForm, steps: u}); }}} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" disabled={idx === 0}>▲</button>
                      <button type="button" onClick={() => { if (idx < editForm.steps.length-1) { const u = [...editForm.steps]; [u[idx], u[idx+1]] = [u[idx+1], u[idx]]; u.forEach((s,i) => s.position = i+1); setEditForm({...editForm, steps: u}); }}} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" disabled={idx === editForm.steps.length-1}>▼</button>
                      <button type="button" onClick={() => { const u = editForm.steps.filter((_,i) => i !== idx); u.forEach((s,i) => s.position = i+1); setEditForm({...editForm, steps: u}); }} className="p-1 text-red-400 hover:text-red-600">✕</button>
                    </div>
                  ))}
                </div>
                <button type="button" onClick={() => setEditForm({...editForm, steps: [...editForm.steps, { position: editForm.steps.length + 1, text: '' }]})} className="mt-2 text-sm text-blue-600 hover:text-blue-800">+ Add Step</button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Points</label>
                  <input type="number" value={editForm.base_points || ''} onChange={(e) => setEditForm({...editForm, base_points: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" min="1" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Minutes</label>
                  <input type="number" value={editForm.estimated_minutes || ''} onChange={(e) => setEditForm({...editForm, estimated_minutes: parseInt(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg" min="1" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Difficulty</label>
                  <select value={editForm.difficulty} onChange={(e) => setEditForm({...editForm, difficulty: e.target.value})} className="w-full px-3 py-2 border rounded-lg">
                    <option value="easy">Easy</option>
                    <option value="medium">Medium</option>
                    <option value="hard">Hard</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editForm.requires_verification} onChange={(e) => setEditForm({...editForm, requires_verification: e.target.checked})} />
                  <span className="text-sm">Requires Verification</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={editForm.requires_photo} onChange={(e) => setEditForm({...editForm, requires_photo: e.target.checked})} />
                  <span className="text-sm">Requires Photo</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Default due time</label>
                <input
                  type="time"
                  value={editForm.default_due_time}
                  onChange={(e) => setEditForm({...editForm, default_due_time: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Applied when this chore is assigned or auto-scheduled. Leave blank for 8 PM. Affects reminder and overdue notifications.
                </p>
              </div>

              {rulesEnabled && (
                <div className="border-t pt-4">
                  <label className="flex items-center gap-2 mb-3">
                    <input type="checkbox" checked={editRuleOn} onChange={(e) => setEditRuleOn(e.target.checked)} />
                    <span className="text-sm font-medium">Schedule this chore automatically</span>
                  </label>
                  {editRuleOn ? (
                    <ChoreRuleEditor kids={children} value={editRule} onChange={setEditRule} />
                  ) : (
                    <p className="text-xs text-gray-500">
                      {editRuleExisted
                        ? 'Saving will stop scheduling this chore and remove its upcoming unstarted assignments.'
                        : 'Turn on to hand this chore out on a schedule: to each kid, as a team, in turns, or open to claim.'}
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
              <button onClick={() => { setShowEditModal(false); setEditingChoreId(null); }} className="px-4 py-2 text-gray-600 hover:text-gray-900">Cancel</button>
              <button onClick={handleSaveEdit} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};